import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiShield, FiRefreshCw, FiDatabase, FiActivity, FiZap, FiHeadphones, 
  FiCheck, FiChevronDown, FiSearch, 
  FiTool, FiAlertCircle, FiClock, FiCheckCircle 
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const WebsiteManagement = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Page SEO Metadata
    document.title = "Website Management Services | ManagedSite.net";
    
    const description = "ManagedSite.net provides WordPress maintenance, security, backups, monitoring, performance support and technical website management for businesses and agencies.";
    const title = "Website Management Services | ManagedSite.net";

    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);

  const whyMatters = [
    {
      title: "Security Risks",
      text: "Outdated plugins, themes and software can expose websites to known vulnerabilities.",
      icon: FiShield
    },
    {
      title: "Unexpected Downtime",
      text: "Hosting, DNS, SSL and domain problems can take a website offline without warning.",
      icon: FiAlertCircle
    },
    {
      title: "Broken Updates",
      text: "Plugin, theme and platform updates can create conflicts or affect important functionality.",
      icon: FiRefreshCw
    },
    {
      title: "Missed Problems",
      text: "Forms, checkout pages, backups and email delivery can fail without being immediately noticed.",
      icon: FiSearch
    }
  ];

  const coreServices = [
    {
      title: "WordPress Updates",
      description: "Ongoing WordPress core, plugin and theme updates with review and troubleshooting when problems occur.",
      items: ["WordPress core updates", "Plugin updates", "Theme updates", "Compatibility checks", "Update-related troubleshooting"]
    },
    {
      title: "Vulnerability Protection",
      description: "Proactive monitoring and protection using Patchstack and WP Cerber.",
      items: ["Known vulnerability alerts", "Security hardening", "Login protection", "Firewall configuration", "Suspicious activity monitoring"]
    },
    {
      title: "Backups and Recovery",
      description: "Offsite website backups with Amazon S3 storage and recovery assistance.",
      items: ["Automated backups", "Offsite storage", "Backup retention", "Restore testing when appropriate", "Recovery assistance"]
    },
    {
      title: "Website Monitoring",
      description: "Monitoring for website availability and critical infrastructure issues.",
      items: ["Uptime monitoring", "SSL monitoring", "Domain expiration monitoring", "DNS monitoring", "Blacklist monitoring"]
    },
    {
      title: "Performance Support",
      description: "Ongoing review and troubleshooting to help keep websites stable and responsive.",
      items: ["Caching review", "Database cleanup", "Image optimization", "Performance troubleshooting", "Plugin impact review"]
    },
    {
      title: "Technical Support",
      description: "Help with website issues, plugin conflicts, forms, integrations, WooCommerce and other technical problems.",
      items: ["WordPress troubleshooting", "Form issues", "Plugin conflicts", "Website edits", "WooCommerce support"]
    }
  ];

  const protectionStack = [
    { title: "Patchstack", text: "Vulnerability monitoring and protection for known WordPress plugin, theme and core issues." },
    { title: "WP Cerber", text: "Website security, login protection, traffic monitoring and malware defense." },
    { title: "WP Time Capsule", text: "Incremental website backups designed to reduce server load and support reliable recovery." },
    { title: "Amazon S3", text: "Offsite backup storage separated from the website's hosting environment." },
    { title: "MonSpark", text: "Monitoring for uptime, SSL, domains, DNS and other digital infrastructure." },
    { title: "Postmark", text: "Reliable transactional email delivery for forms, password resets, order notifications and website messages when needed." }
  ];

  const monitoringList = [
    "Website uptime", "SSL certificate status", "Domain expiration", "DNS changes", 
    "Malware and blacklist status", "Backup completion", "Website vulnerabilities", 
    "Critical forms", "Checkout functionality", "Transactional email delivery"
  ];

  const complexityLevels = [
    { title: "Standard Business Websites", text: "Informational websites, professional service sites, blogs and lead-generation websites." },
    { title: "WooCommerce and E-commerce", text: "Online stores that require more frequent backups, checkout monitoring and higher-priority support." },
    { title: "Membership and Integrated Websites", text: "Membership platforms, learning systems, directories and websites connected to third-party services." }
  ];

  const steps = [
    { title: "Website Review", text: "We review the website, hosting, plugins, themes, security, backups and current technical condition." },
    { title: "Onboarding", text: "We connect monitoring, backup, security and reporting systems." },
    { title: "Ongoing Management", text: "We perform scheduled maintenance, monitor issues and respond according to the selected service level." },
    { title: "Reporting and Improvement", text: "We provide reports, identify recurring risks and recommend improvements when needed." }
  ];

  const faqs = pageFaqs['/services/website-management'];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-[#FAF8F3] overflow-hidden pt-24 pb-20 lg:pt-32 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-start">
            <div className="lg:col-span-7 text-center lg:text-left mb-12 lg:mb-0 lg:pt-4">
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-6">Website Management</div>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-6">
                Keep Every Website Secure, Updated and Working
              </h1>
              <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0 font-medium">
                Ongoing website maintenance, security, backups, monitoring, and technical support — for your own site or a portfolio of client sites.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link to="/digital-review" className="home-button">
                  Request a Website Review
                </Link>
                <Link to="/agency-plans" className="home-button-secondary">
                  View Agency Plans
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-[2rem] bg-[#EAF1EC] border border-[#3B7C6D]/20 shadow-2xl p-6 sm:p-8">
                <div className="absolute top-0 left-0 w-full h-2 bg-[#174C3C]"></div>
                <div className="space-y-3">
                  {[
                    { icon: FiRefreshCw, label: "Website Updates", status: "Active" },
                    { icon: FiShield, label: "Security Monitoring", status: "Protected" },
                    { icon: FiDatabase, label: "Offsite Backups", status: "Completed" },
                    { icon: FiActivity, label: "Uptime", status: "Online" },
                    { icon: FiCheckCircle, label: "SSL and DNS", status: "Monitored" },
                    { icon: FiZap, label: "Performance", status: "Stable" }
                  ].map((item, i) => (
                    <div key={i} className="bg-white px-4 py-3 rounded-2xl flex items-center justify-between border border-gray-50">
                      <div className="flex items-center gap-3">
                        <div className="bg-[#EAF1EC] p-2.5 rounded-xl text-[#174C3C]">
                          <SafeIcon icon={item.icon} className="w-5 h-5" />
                        </div>
                        <span className="font-extrabold text-[#17211E]">{item.label}</span>
                      </div>
                      <span className="text-sm font-bold text-[#3B7C6D] bg-[#EAF1EC] px-3 py-1 rounded-full">{item.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Website Management Matters */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">A Website Needs More Than Occasional Updates</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              Websites depend on plugins, themes, hosting, domains, DNS, SSL certificates, email delivery and third-party services. When one part fails, the website can become slow, insecure or unavailable.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyMatters.map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] hover:shadow-md transition-all">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Website Management Services */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What Website Management Includes</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {coreServices.map((service, i) => (
              <div key={i} className="bg-white p-10 md:p-12 rounded-[2rem] border border-[#EAF1EC] shadow-sm flex flex-col h-full">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{service.title}</h3>
                <p className="text-xl text-[#17211E]/75 mb-10 leading-relaxed font-medium">{service.description}</p>
                <div className="flex-grow">
                  <div className="text-sm font-bold text-[#3B7C6D] uppercase tracking-wider mb-6">Included:</div>
                  <ul className="space-y-4">
                    {service.items.map((item, j) => (
                      <li key={j} className="flex items-start text-[#17211E]/80 text-lg font-medium">
                        <SafeIcon icon={FiCheck} className="w-5 h-5 text-[#174C3C] mr-4 mt-1 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Protection Stack Section */}
      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">A Complete Website Protection Stack</h2>
          <p className="text-xl text-[#17211E]/75 mb-20 font-medium leading-relaxed max-w-3xl mx-auto">
            ManagedSite.net combines specialized tools and human oversight to reduce website risk.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {protectionStack.map((tool, i) => (
              <div key={i} className="bg-white p-10 rounded-3xl border border-[#3B7C6D]/10 text-left shadow-sm">
                <h3 className="text-2xl font-extrabold text-[#174C3C] mb-4">{tool.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{tool.text}</p>
              </div>
            ))}
          </div>
          <p className="text-[#17211E]/50 font-bold italic text-lg mb-12">
            Third-party services remain active only while the website is enrolled in an eligible ManagedSite.net plan.
          </p>
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              {['Essential Care', 'Managed Care', 'Advanced Care'].map((plan) => (
                <div key={plan} className="bg-white px-6 py-8 rounded-3xl border border-[#3B7C6D]/10 shadow-sm">
                  <div className="text-sm font-bold uppercase tracking-widest text-[#17211E]/45 mb-3">
                    {plan}
                  </div>
                  <div className="mt-2 text-sm font-bold uppercase tracking-widest text-[#17211E]/40">
                    Per site
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[#17211E]/60 font-medium mb-8">
              Businesses use these same three plans. Send the site for a Digital Review — we recommend a level and quote from there. Agency partner rates start at $59 per site each month so agencies can set their own client pricing. Volume pricing is available at 10 or more active sites.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/get-started" className="home-button">
                Get started
              </Link>
              <Link to="/digital-review" className="home-button-secondary">
                Request a Digital Review
              </Link>
            </div>
            <p className="mt-6 text-[#17211E]/60 font-medium">
              Agencies:{' '}
              <Link to="/agency-plans" className="font-bold text-[#174C3C] underline decoration-2 underline-offset-4">
                see agency plans
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Monitoring Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">More Than Uptime Monitoring</h2>
              <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed">
                A website can appear online while important systems are already failing.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mb-12">
                {monitoringList.map((item, i) => (
                  <div key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <div className="w-7 h-7 bg-[#EAF1EC] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <SafeIcon icon={FiCheck} className="w-4 h-4 text-[#174C3C]" />
                    </div>
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-lg text-[#17211E]/60 italic font-medium">
                Monitoring identifies potential problems. Some remediation work may require additional technical support or a higher service level.
              </p>
            </div>
            <div className="bg-[#FAF8F3] rounded-[3rem] p-12 border border-[#EAF1EC] relative overflow-hidden">
               <div className="relative z-10 space-y-8">
                  <div className="flex items-center justify-between pb-6 border-b border-[#17211E]/10">
                    <span className="text-2xl font-black text-[#17211E]">Status Dashboard</span>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-green-500 animate-pulse"></div>
                      <span className="text-sm font-bold uppercase tracking-widest text-green-600">Live</span>
                    </div>
                  </div>
                  <div className="space-y-6">
                    {['Critical Forms', 'Checkout API', 'Transactional Email'].map((m, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xl font-bold text-[#17211E]/80">{m}</span>
                        <span className="text-sm font-black text-[#174C3C] bg-white px-4 py-1.5 rounded-full border border-[#EAF1EC]">Healthy</span>
                      </div>
                    ))}
                  </div>
               </div>
               <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-[#EAF1EC] rounded-full opacity-50"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Website Types We Support */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Website Management for Different Levels of Complexity</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            {complexityLevels.map((level, i) => (
              <div key={i} className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm flex flex-col text-center">
                <h3 className="text-2xl font-extrabold text-[#17211E] mb-6">{level.title}</h3>
                <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium mb-8 flex-grow">{level.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-[#17211E]/50 font-bold italic text-lg">
            Complex websites may require an advanced management plan after review.
          </p>
        </div>
      </section>

      {/* How Management Works */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center">How Website Management Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Support and Service Time */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Maintenance and Technical Support Are Different</h2>
            <p className="text-xl text-[#17211E]/75 leading-relaxed font-medium">
              Routine scheduled maintenance is included according to the selected plan. Technical requests, troubleshooting and project work may use included support time or be billed separately.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16">
            <div className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#174C3C] mb-8 border-b border-[#EAF1EC] pb-6">Routine Management</h3>
              <ul className="space-y-6">
                {["WordPress updates", "Security monitoring", "Backups", "Uptime monitoring", "SSL monitoring", "Domain and DNS monitoring", "Monthly reporting"].map((item, i) => (
                  <li key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#174C3C] mr-4 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-12 rounded-[2.5rem] border border-[#EAF1EC] shadow-sm">
              <h3 className="text-2xl font-extrabold text-[#3B7C6D] mb-8 border-b border-[#EAF1EC] pb-6">Technical Support</h3>
              <ul className="space-y-6">
                {["Website troubleshooting", "Content changes", "Plugin configuration", "Integration problems", "WooCommerce issues", "Performance work", "Development requests"].map((item, i) => (
                  <li key={i} className="flex items-center text-xl font-bold text-[#17211E]">
                    <div className="w-6 h-6 bg-[#EAF1EC] rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                      <div className="w-2 h-2 bg-[#174C3C] rounded-full"></div>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <p className="text-center text-[#17211E]/50 font-bold italic text-lg">
            Unused included technical support time does not roll over.
          </p>
        </div>
      </section>

      {/* White-Label Agency Section */}
      <section className="py-32 bg-[#174C3C] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Agencies</div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-10 leading-tight">Website Management Delivered Behind Your Brand</h2>
              <p className="text-2xl text-[#FAF8F3]/80 mb-12 font-medium leading-relaxed">
                ManagedSite.net handles updates, backups, monitoring, security and technical support while your agency owns the client relationship.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-6 mb-16">
                {["White-label delivery", "Agency pricing", "Client-ready reports", "Volume pricing for 10 or more websites", "Technical support blocks", "Managed hosting options", "No direct solicitation of agency clients"].map((item, i) => (
                  <div key={i} className="flex items-start text-xl font-bold">
                    <SafeIcon icon={FiCheck} className="w-6 h-6 text-[#D7B77A] mr-4 flex-shrink-0 mt-1" />
                    {item}
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/agency-services/white-label-wordpress" className="home-button-on-dark">
                  How White-Label Works
                </Link>
                <Link to="/agency-plans" className="home-button-on-dark-secondary">
                  View White-Label Plans
                </Link>
              </div>
            </div>
            <div className="hidden lg:block relative select-none opacity-50">
               <div className="bg-white/5 rounded-[3rem] p-16 border border-white/10 backdrop-blur-sm">
                  <div className="text-[#D7B77A] text-6xl font-black mb-10">WL</div>
                  <div className="h-1.5 w-24 bg-[#D7B77A] mb-10"></div>
                  <div className="space-y-6">
                    <div className="h-4 bg-white/20 rounded-full w-full"></div>
                    <div className="h-4 bg-white/20 rounded-full w-5/6"></div>
                    <div className="h-4 bg-white/20 rounded-full w-4/6"></div>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Direct Business Routing Section */}
      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-[2.5rem] p-12 md:p-20 shadow-xl border border-[#EAF1EC] text-center max-w-5xl mx-auto">
            <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">For Businesses</div>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#17211E] mb-8 leading-tight">Need Website Management for Your Own Business?</h2>
            <p className="text-xl text-[#17211E]/75 mb-12 font-medium leading-relaxed max-w-3xl mx-auto">
              Tell us what is happening with the website, and we will help work out the next step.
            </p>
            <Link to="/digital-review" className="home-button">
              Tell Us What You Need Help With
            </Link>
          </div>
        </div>
      </section>

      {/* Additional Website Services */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">Additional Website Services</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              "Website migrations", "Malware cleanup", "Performance optimization", "WooCommerce monitoring", 
              "Form testing", "Email Reliability", "Termageddon policy setup", "Managed hosting", 
              "DNS and SSL work", "Website redesigns", "Development and integrations", "Emergency recovery"
            ].map((item, i) => (
              <div key={i} className="bg-[#FAF8F3] p-6 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-lg flex items-center gap-4">
                <div className="w-2 h-2 rounded-full bg-[#174C3C]"></div>
                {item}
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/services" className="home-button-secondary">
              Explore All Services
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Accordion - Updated for Accessibility and SEO */}
      <section className="py-24 bg-[#FAF8F3] border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-[#FAF8F3] transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div 
                  id={`faq-answer-${i}`}
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-10 tracking-tight leading-tight">
            Give Every Website the Ongoing Attention It Needs
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed">
            Dependable updates, backups, monitoring, security, and technical support for the websites you are responsible for.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/digital-review" className="home-button-on-dark">
              Request a Website Review
            </Link>
            <Link to="/agency-plans" className="home-button-on-dark-secondary">
              View Agency Plans
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebsiteManagement;